import React, { Suspense } from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import { lookupsJson, InlandMarineTypes as defaultInlandMarineTypes } from '@ourbranch/lookups';
import { FarmScheduleCoverageType } from '@ourbranch/lookups';
import { brandConfig } from 'brand-config';

import { currencyFormatter } from 'core/helpers/formatters';
import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import Field from 'core/components/form/form.v2';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from './collapsible-list-item';
import Grid from '@material-ui/core/Grid';
import alertIcon from 'core/assets/svg/alert-white-bold.svg';

import useStyles from './sub-risk.styles';
import { BrandFormField } from 'common/components/farm/brand-form-field';

const defaultInlandMarineTypesOptions = Object.values(defaultInlandMarineTypes).map((type) => ({
  id: type.id,
  value: type.value
}));

const validationSchema = (state) =>
  Yup.object().shape({
    category: Yup.string().required('Item type is required'),
    deductible: Yup.number().when('category', {
      is: (val) => ['scheduledGlass', 'computerHardware', 'computerSoftware'].includes(val),
      then: (schema) => schema.required('Deductible is required'),
      otherwise: (schema) => (state === 'IL' ? schema.nullable() : schema.required('Deductible is required'))
    }),
    limit: Yup.number().required('Coverage limit is required'),
    description: Yup.string().nullable().required('Description is required'),
    year: Yup.number().when('category', {
      is: 'boats',
      then: (schema) => schema.required('Year is required')
    }),
    horsepower: Yup.number().when('category', {
      is: 'boats',
      then: (schema) => schema.required('Horsepower is required')
    }),
    serialNumber: Yup.string().when('category', {
      is: (val) => val === 'boats' || val === 'unmannedAircraft',
      then: (schema) => schema.required('Serial Number is required')
    }),
    classification: Yup.string().when('category', {
      is: 'boats',
      then: (schema) => schema.required('Classification is required')
    }),
    make: Yup.string().when('category', {
      is: 'unmannedAircraft',
      then: (schema) => schema.required('Make and Model is required')
    }),
    dimensions: Yup.string().when('category', {
      is: 'boatDock',
      then: (schema) => schema.required('Dimensions are required')
    })
  });

const alertMessages = {
  boatDock: 'Boat docks are excluded from damage caused by collapse of weight of ice, snow or sleet.',
  golfCarts:
    'Adding a golf cart will enable liability coverage. The golf cart must be logged in both this section and under all-terrain vehicle liability coverage.'
};

const InlandMarineForm = ({ fieldName, disabled, riskType, address, coverages }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const scheduleCoverages = getIn(values, fieldName) || [];
  const items = scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.SPP)?.items || [];

  const state =
    getIn(values, 'correctedAddress.state') ||
    getIn(values, 'locations.0.address.state') ||
    getIn(values, 'risks.0.address.state');

  const isInlandMarineCoverageEnabled = !brandConfig.farm[state]?.[riskType]?.hiddenCoverages?.includes('inlandMarine');
  const brandSpecificTypes = (
    brandConfig.farm[state]?.[riskType]?.inlandMarine?.itemTypes || defaultInlandMarineTypesOptions
  ).sort((a, b) => a.id.localeCompare(b.id));
  const brandDeductibleOptions =
    brandConfig.farm[state]?.[riskType]?.inlandMarine?.inlandMarineDeductible ||
    lookupsJson.farm.inlandMarineDeductible;

  if (!isInlandMarineCoverageEnabled) {
    return null;
  }

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Inland marine coverage</div>}
      initialOpen={false}
    >
      <FormSectionWrapper
        sectionTitle="Scheduled items"
        totalLabel="items"
        totalCount={items?.length || 0}
        emptyStateTitle="There are no items added yet"
      >
        {/* list items */}
        {items.map((item, index) => {
          const itemName = brandSpecificTypes.find((type) => type.id === item.category)?.value;
          const rightSideValues = [];

          if (item.deductible) {
            rightSideValues.push({ label: 'Deductible', value: `$${item.deductible}` });
          }
          rightSideValues.push({ label: 'Limit', value: `$${item.limit}` });

          const detailFields = [];

          // Add category-specific fields
          if (item.category === 'boatDock' && item.extendedDetails?.dimensions) {
            detailFields.push({
              name: 'dimensions',
              label: 'Dimensions',
              value: item.extendedDetails.dimensions,
              size: 12
            });
          }

          if (item.category === 'boats') {
            if (item.extendedDetails?.year) {
              detailFields.push({
                name: 'year',
                label: 'Year',
                value: item.extendedDetails.year,
                size: 6
              });
            }
            if (item.extendedDetails?.horsepower) {
              detailFields.push({
                name: 'horsepower',
                label: 'Horsepower',
                value: item.extendedDetails.horsepower,
                size: 6
              });
            }
            if (item.extendedDetails?.serialNumber) {
              detailFields.push({
                name: 'serialNumber',
                label: 'Serial Number',
                value: item.extendedDetails.serialNumber,
                size: 6
              });
            }
            if (item.extendedDetails?.classification) {
              detailFields.push({
                name: 'classification',
                label: 'Classification',
                value: item.extendedDetails.classification,
                size: 6
              });
            }
          }

          if (item.category === 'unmannedAircraft') {
            if (item.extendedDetails?.make) {
              detailFields.push({
                name: 'make',
                label: 'Make and Model',
                value: item.extendedDetails.make,
                size: 6
              });
            }
            if (item.extendedDetails?.serialNumber) {
              detailFields.push({
                name: 'serialNumber',
                label: 'Serial Number',
                value: item.extendedDetails.serialNumber,
                size: 6
              });
            }
          }

          // Always add description
          detailFields.push({
            name: 'description',
            label: 'Description',
            value: item.description || 'Not provided',
            size: 12
          });

          // Add deductible fields
          if (item.extendedDetails?.deductibleAllOther) {
            detailFields.push({
              name: 'deductibleAllOther',
              label: 'All peril deductible',
              value: `$${item.extendedDetails.deductibleAllOther}`,
              size: 6
            });
          }

          return (
            <CollapsibleListItem
              key={`item-${index}`}
              title={itemName}
              rightSideValues={rightSideValues}
              detailFields={detailFields}
              alertMessage={['boatDock', 'golfCarts'].includes(item.category) ? alertMessages[item.category] : null}
              onDelete={() => {
                const newItems = items.filter((_, checkIndex) => checkIndex !== index);
                const currentScheduleCoverages =
                  getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.SPP) || [];
                setFieldValue(`${fieldName}`, [
                  ...currentScheduleCoverages,
                  {
                    coverageType: FarmScheduleCoverageType.SPP,
                    items: newItems
                  }
                ]);
                setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.SPP}`);
              }}
              disabled={disabled}
              keyPrefix={`item-${index}`}
            />
          );
        })}

        {/* add form */}
        <div className={classes.addFormContainer}>
          <Formik
            initialValues={{
              category: undefined,
              deductible: state === 'IL' ? null : 250,
              limit: undefined,
              description: undefined,
              deductibleAllOther: undefined
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema(state)}
            onSubmit={(val, { resetForm }) => {
              const item = val;
              Object.keys(item).forEach((key) => {
                if (item[key] === '') {
                  item[key] = null;
                }
              });
              const { category, deductible, description, limit, ...rest } = item;
              const newItem = {
                category,
                deductible,
                description,
                limit,
                ...(Object.keys(rest).length > 0
                  ? {
                      extendedDetails: {
                        ...rest
                      }
                    }
                  : {})
              };
              const currentScheduleCoverages =
                getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.SPP) || [];
              const currentSPPItems =
                getIn(values, fieldName)?.find((item) => item.coverageType === FarmScheduleCoverageType.SPP)?.items ||
                [];
              setFieldValue(`${fieldName}`, [
                ...currentScheduleCoverages,
                {
                  coverageType: FarmScheduleCoverageType.SPP,
                  items: [...currentSPPItems, newItem]
                }
              ]);
              setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.SPP}`);

              resetForm();
            }}
          >
            {({ values, submitForm }) => {
              return (
                <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                  <Field
                    xs={6}
                    type="select"
                    id="category"
                    name="category"
                    mode="dark"
                    label="Type of item"
                    options={brandSpecificTypes}
                    disabled={disabled}
                  />
                  {state === 'IL' ? (
                    <Field
                      xs={6}
                      type="numeric"
                      id="limit"
                      name="limit"
                      mode="dark"
                      label="Coverage Limit"
                      format={{ thousandSeparator: true, prefix: '$' }}
                      disabled={disabled}
                    />
                  ) : (
                    <Field
                      xs={6}
                      type="select"
                      id="deductible"
                      name="deductible"
                      mode="dark"
                      label="Deductible"
                      options={lookupsJson.farm.inlandFloodDeductible}
                      disabled={disabled}
                    />
                  )}

                  {values.category &&
                    [
                      'coinCollection',
                      'fineArts',
                      'furs',
                      'golfCarts',
                      'golfEquipment',
                      'guns',
                      'jewelry',
                      'miscellaneousProperty',
                      'musicalInstruments',
                      'personalTools',
                      'silverware',
                      'sportingEquipment',
                      'tack'
                    ].includes(values.category) && (
                      <>
                        {state !== 'IL' && (
                          <Field
                            xs={12}
                            type="numeric"
                            id="limit"
                            name="limit"
                            mode="dark"
                            label="Coverage Limit"
                            format={{ thousandSeparator: true, prefix: '$' }}
                            disabled={disabled}
                          />
                        )}
                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description"
                          disabled={disabled}
                        />
                      </>
                    )}
                  {values.category &&
                    ['scheduledGlass', 'computerHardware', 'computerSoftware'].includes(values.category) && (
                      <>
                        {state === 'IL' ? (
                          <Field
                            xs={12}
                            type="select"
                            id="deductible"
                            name="deductible"
                            mode="dark"
                            label="Deductible"
                            options={lookupsJson.farm.inlandFloodDeductible}
                            disabled={disabled}
                          />
                        ) : (
                          <Field
                            xs={12}
                            type="numeric"
                            id="limit"
                            name="limit"
                            mode="dark"
                            label="Coverage Limit"
                            format={{ thousandSeparator: true, prefix: '$' }}
                            disabled={disabled}
                          />
                        )}
                        <Field
                          xs={12}
                          type="textarea"
                          id="description"
                          name="description"
                          mode="dark"
                          label="Description"
                          disabled={disabled}
                        />
                      </>
                    )}

                  {values.category && values.category === 'boatDock' && (
                    <>
                      {state !== 'IL' && (
                        <Field
                          xs={6}
                          type="numeric"
                          id="limit"
                          name="limit"
                          mode="dark"
                          label="Coverage Limit"
                          format={{ thousandSeparator: true, prefix: '$' }}
                          disabled={disabled}
                        />
                      )}
                      <Field
                        xs={state === 'IL' ? 12 : 6}
                        type="string"
                        id="dimensions"
                        name="dimensions"
                        mode="dark"
                        label="Dimensions"
                        disabled={disabled}
                      />
                      <Field
                        xs={12}
                        type="textarea"
                        id="description"
                        name="description"
                        mode="dark"
                        label="Description"
                        disabled={disabled}
                      />
                    </>
                  )}

                  {values.category && values.category === 'boats' && (
                    <>
                      {state !== 'IL' && (
                        <Field
                          xs={4}
                          type="numeric"
                          id="limit"
                          name="limit"
                          mode="dark"
                          label="Coverage Limit"
                          format={{ thousandSeparator: true, prefix: '$' }}
                          disabled={disabled}
                        />
                      )}
                      <Field
                        xs={state === 'IL' ? 6 : 4}
                        type="numeric"
                        id="year"
                        name="year"
                        mode="dark"
                        label="Year"
                        disabled={disabled}
                      />
                      <Field
                        xs={state === 'IL' ? 6 : 4}
                        type="numeric"
                        id="horsepower"
                        name="horsepower"
                        mode="dark"
                        label="Horsepower"
                        disabled={disabled}
                      />
                      <Field
                        xs={6}
                        type="string"
                        id="serialNumber"
                        name="serialNumber"
                        mode="dark"
                        label="Serial Number"
                        disabled={disabled}
                      />
                      <Field
                        xs={6}
                        type="select"
                        id="classification"
                        name="classification"
                        mode="dark"
                        label="Classification"
                        options={lookupsJson.farm.boatClassifications}
                        disabled={disabled}
                      />
                      <Field
                        xs={12}
                        type="textarea"
                        id="description"
                        name="description"
                        mode="dark"
                        label="Description"
                        disabled={disabled}
                      />
                    </>
                  )}

                  {values.category && values.category === 'unmannedAircraft' && (
                    <>
                      {state !== 'IL' && (
                        <Field
                          xs={4}
                          type="numeric"
                          id="limit"
                          name="limit"
                          mode="dark"
                          label="Coverage Limit"
                          format={{ thousandSeparator: true, prefix: '$' }}
                          disabled={disabled}
                        />
                      )}
                      <Field
                        xs={state === 'IL' ? 6 : 4}
                        type="string"
                        id="make"
                        name="make"
                        mode="dark"
                        label="Make and Model"
                        disabled={disabled}
                      />
                      <Field
                        xs={state === 'IL' ? 6 : 4}
                        type="string"
                        id="serialNumber"
                        name="serialNumber"
                        mode="dark"
                        label="Serial Number"
                        disabled={disabled}
                      />

                      <Field
                        xs={12}
                        type="textarea"
                        id="description"
                        name="description"
                        mode="dark"
                        label="Description"
                        disabled={disabled}
                      />
                    </>
                  )}

                  {values.category && (
                    <Suspense fallback={null}>
                      <BrandFormField
                        xs={12}
                        id="deductibleAllOther"
                        name="deductibleAllOther"
                        type="select"
                        options={brandDeductibleOptions}
                        mode="dark"
                        label="All peril deductible"
                        disabled={disabled}
                        riskType={riskType}
                        state={state}
                        coverages={coverages}
                      />
                    </Suspense>
                  )}

                  {values.category && (
                    <Grid container alignItems="center" className={classes.singleBottom}>
                      {['boatDock', 'golfCarts'].includes(values.category) && (
                        <div className={classes.alert}>
                          <img src={alertIcon} className={classes.alertIcon} alt="Alert Icon" />
                          <div className={classes.alertMessage}>{alertMessages[values.category]}</div>
                        </div>
                      )}
                      <Button
                        type="button"
                        mode="big"
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        disabled={disabled}
                        onClick={submitForm}
                      >
                        Add Item
                      </Button>
                    </Grid>
                  )}
                </Grid>
              );
            }}
          </Formik>
        </div>
      </FormSectionWrapper>
    </SubRiskCollapsibleSection>
  );
};

InlandMarineForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  riskType: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  address: PropTypes.object
};

InlandMarineForm.defaultProps = {
  disabled: false,
  address: {}
};

export default observer(InlandMarineForm);
